/**
 * T-085 — Authenticated API client for the tnd-support SPA (spec §8 src/api.ts).
 *
 * Single entry point for every JSON route the Pages Functions backend
 * exposes. shared/api.ts is the wire contract: DTOs are imported from there,
 * never redefined locally. Behavior guarantees:
 *
 *  - Every request carries `Authorization: Bearer <operator token>` obtained
 *    from the caller-supplied `getToken` callback at call time — the client
 *    never stores the token itself (session state owns it, sessionStorage
 *    only, spec §3).
 *  - `GET /api/download/<token>` is intentionally NOT wrapped: it is a
 *    byte-stream capability route, not JSON. Use {@link ApiClient.resolveUrl}
 *    to absolutize the one-time URL from `CreateLinkResponse.url`.
 *  - Non-2xx bodies matching {@link ApiErrorBody} surface as {@link ApiError}
 *    with the stable machine-readable code; anything else (proxy error
 *    pages, truncated bodies) is normalized to a generic ApiError without
 *    echoing the raw payload (spec §3 leak rules).
 *  - A 401 response invokes `onUnauthorized` exactly once per request so the
 *    session layer can drop the rejected token.
 *  - Transport failures raise {@link NetworkError}; caller abort signals
 *    propagate as the original AbortError so views can ignore cancellations.
 *  - Requests use `cache: 'no-store'` and `credentials: 'omit'` — bearer
 *    auth only, never cookies (spec §3).
 */

import {
  API_ERROR_STATUS,
  isApiErrorBody,
  type ApiErrorCode,
  type AuditListQuery,
  type AuditListResponse,
  type CreateLinkRequest,
  type CreateLinkResponse,
  type LinksListQuery,
  type LinksListResponse,
  type ObjectsListQuery,
  type ObjectsListResponse,
  type ProvidersHealthResponse,
  type ReleaseArtifactsResponse,
  type ReleasesResponse,
  type RevokeLinkResponse,
  type RunArtifactsResponse,
  type RunsListQuery,
  type RunsListResponse,
} from '../shared/api';

/**
 * Default API origin. Same convention as the scaffold health check: empty
 * string means same-origin (Pages Functions under /api in production, the
 * Vite dev proxy in development). The `typeof` guard keeps the module
 * importable outside Vite (plain Node/Bun smoke harnesses) where
 * `import.meta.env` does not exist.
 */
export const DEFAULT_API_BASE_URL: string =
  typeof import.meta.env === 'undefined' ? '' : (import.meta.env.VITE_API_BASE_URL ?? '');

/** Error raised for every non-2xx API response. */
export class ApiError extends Error {
  /** Stable machine-readable code from the shared error envelope. */
  readonly code: ApiErrorCode;
  /** HTTP status actually observed (matches API_ERROR_STATUS[code] for envelope errors). */
  readonly status: number;

  constructor(code: ApiErrorCode, status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

/** Error raised when the request never received an HTTP response. */
export class NetworkError extends Error {
  constructor(message: string, cause: unknown) {
    super(message, { cause });
    this.name = 'NetworkError';
  }
}

export interface ApiClientOptions {
  /** API origin; defaults to {@link DEFAULT_API_BASE_URL} (same-origin). */
  baseUrl?: string;
  /** Called before every request; null means "no session token". */
  getToken: () => string | null;
  /** Invoked once per request that the server rejects with 401. */
  onUnauthorized?: () => void;
  /** Injectable for tests; defaults to the global fetch. */
  fetchImpl?: typeof fetch;
}

/** Typed client for the tnd-support JSON API (spec §5). */
export interface ApiClient {
  /** GET /api/releases — Page 1 release selector. */
  listReleases(signal?: AbortSignal): Promise<ReleasesResponse>;
  /** GET /api/releases/:version/artifacts — Page 1 artifact table. */
  listReleaseArtifacts(version: string, signal?: AbortSignal): Promise<ReleaseArtifactsResponse>;
  /** GET /api/links — Page 3 managed/direct link list. */
  listLinks(query?: LinksListQuery, signal?: AbortSignal): Promise<LinksListResponse>;
  /** POST /api/links — issue a managed link or direct GCS V4 URL (spec §4). */
  createLink(request: CreateLinkRequest, signal?: AbortSignal): Promise<CreateLinkResponse>;
  /** POST /api/links/:id/revoke — 200 on managed links; 409 `non_revocable` on gcs_v4. */
  revokeLink(linkId: string, signal?: AbortSignal): Promise<RevokeLinkResponse>;
  /** GET /api/objects — Page 3 allowlisted object browser. */
  listObjects(query: ObjectsListQuery, signal?: AbortSignal): Promise<ObjectsListResponse>;
  /** GET /api/audit — Page 3 application audit events. */
  listAudit(query?: AuditListQuery, signal?: AbortSignal): Promise<AuditListResponse>;
  /** GET /api/runs — Page 4 build-package-release.yml run browser. */
  listRuns(query?: RunsListQuery, signal?: AbortSignal): Promise<RunsListResponse>;
  /** GET /api/runs/:id/artifacts — Page 4 per-run artifact inventory. */
  listRunArtifacts(runId: number, signal?: AbortSignal): Promise<RunArtifactsResponse>;
  /**
   * GET /api/providers/health — live GCS/GitHub credential probes. Always
   * resolves on a reachable API: a rejected credential is reported in the
   * body, not thrown.
   */
  checkProviders(signal?: AbortSignal): Promise<ProvidersHealthResponse>;
  /**
   * Absolutize a server-returned path (e.g. `/api/download/<token>`) against
   * the configured base URL for copyable commands. Absolute http(s) URLs
   * (direct GCS V4 links) pass through unchanged.
   */
  resolveUrl(path: string): string;
}

/** Reverse lookup: HTTP status → canonical error code (first code wins on shared statuses). */
const STATUS_TO_ERROR_CODE: Record<number, ApiErrorCode> = {};
for (const [code, status] of Object.entries(API_ERROR_STATUS) as [ApiErrorCode, number][]) {
  STATUS_TO_ERROR_CODE[status] ??= code;
}

/** Flatten a query DTO to a string/number record, dropping undefined values. */
function asQueryRecord(query: object | undefined): Record<string, string | number> | undefined {
  if (query === undefined) return undefined;
  const record: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(query)) {
    if (typeof value === 'string' || typeof value === 'number') record[key] = value;
  }
  return record;
}

/** Create a typed API client. See {@link ApiClientOptions}. */
export function createApiClient(options: ApiClientOptions): ApiClient {
  const baseUrl = (options.baseUrl ?? DEFAULT_API_BASE_URL).replace(/\/+$/, '');
  const fetchImpl = options.fetchImpl ?? fetch;
  const onUnauthorized = options.onUnauthorized;

  function buildUrl(path: string, query?: Record<string, string | number>): string {
    const url = `${baseUrl}${path}`;
    if (query === undefined) return url;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) params.set(key, String(value));
    const qs = params.toString();
    return qs === '' ? url : `${url}?${qs}`;
  }

  async function toApiError(response: Response): Promise<ApiError> {
    const fallback = `Unexpected HTTP ${response.status} response from the API`;
    try {
      const body: unknown = await response.json();
      if (isApiErrorBody(body)) {
        return new ApiError(body.error.code, response.status, body.error.message);
      }
    } catch {
      // Non-JSON error body (proxy page, truncation) — fall through to generic.
    }
    return new ApiError(STATUS_TO_ERROR_CODE[response.status] ?? 'internal_error', response.status, fallback);
  }

  async function parseJson<T>(response: Response): Promise<T> {
    try {
      return (await response.json()) as T;
    } catch {
      throw new ApiError('internal_error', response.status, 'Malformed JSON in API response');
    }
  }

  async function request<T>(
    path: string,
    init: { method?: string; query?: Record<string, string | number>; body?: unknown; signal?: AbortSignal } = {},
  ): Promise<T> {
    const token = options.getToken();
    if (token === null) {
      throw new ApiError('unauthorized', 401, 'No operator token in this session');
    }
    const headers = new Headers({
      accept: 'application/json',
      authorization: `Bearer ${token}`,
    });
    let body: string | undefined;
    if (init.body !== undefined) {
      headers.set('content-type', 'application/json');
      body = JSON.stringify(init.body);
    }
    const url = buildUrl(path, init.query);
    let response: Response;
    try {
      response = await fetchImpl(url, {
        method: init.method ?? 'GET',
        headers,
        body,
        signal: init.signal ?? null,
        cache: 'no-store',
        credentials: 'omit',
        redirect: 'error',
      });
    } catch (err) {
      if ((err instanceof DOMException || err instanceof Error) && err.name === 'AbortError') throw err;
      throw new NetworkError(`API request to ${path} failed before an HTTP response`, err);
    }
    if (response.status === 401) onUnauthorized?.();
    if (!response.ok) throw await toApiError(response);
    return parseJson<T>(response);
  }

  return {
    listReleases: (signal) => request<ReleasesResponse>('/api/releases', { signal }),
    listReleaseArtifacts: (version, signal) =>
      request<ReleaseArtifactsResponse>(`/api/releases/${encodeURIComponent(version)}/artifacts`, { signal }),
    listLinks: (query, signal) =>
      request<LinksListResponse>('/api/links', { query: asQueryRecord(query), signal }),
    createLink: (createRequest, signal) =>
      request<CreateLinkResponse>('/api/links', { method: 'POST', body: createRequest, signal }),
    revokeLink: (linkId, signal) =>
      request<RevokeLinkResponse>(`/api/links/${encodeURIComponent(linkId)}/revoke`, {
        method: 'POST',
        body: {},
        signal,
      }),
    listObjects: (query, signal) =>
      request<ObjectsListResponse>('/api/objects', { query: asQueryRecord(query), signal }),
    listAudit: (query, signal) =>
      request<AuditListResponse>('/api/audit', { query: asQueryRecord(query), signal }),
    listRuns: (query, signal) =>
      request<RunsListResponse>('/api/runs', { query: asQueryRecord(query), signal }),
    listRunArtifacts: (runId, signal) =>
      request<RunArtifactsResponse>(`/api/runs/${encodeURIComponent(runId)}/artifacts`, { signal }),
    checkProviders: (signal) =>
      request<ProvidersHealthResponse>('/api/providers/health', { signal }),
    resolveUrl: (path) => {
      if (/^https?:\/\//i.test(path)) return path;
      if (baseUrl === '') return path;
      return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
    },
  };
}
